import './NotificationsList.scss';

import { CheckCircleFilled, ClockCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Pagination, Tag } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';

import { useUserContext } from '@/contexts/UserContext';
import { UserNotification } from '@/model/General/Notification';
import { queryKeys } from '@/repositories/queryKeys';

interface NotificationsListProps {
  notifications: UserNotification[];
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  total?: number;
  refetch: () => void;
}

const NotificationsList = (props: NotificationsListProps) => {
  const { notifications, currentPage, setCurrentPage, pageSize, total, refetch } = props;
  const { markAsReadNotification, notifications: headerNotifications } = useUserContext();
  const handleMarkRead = useCallback(
    (notification: UserNotification) => {
      void markAsReadNotification(notification.id).then(async () => {
        if (!notifications.some(n => n.id == notification.id)) {
          refetch();

          return;
        }
      });
    },
    [markAsReadNotification, notifications, refetch],
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.headerNotifications() });
  }, [headerNotifications, queryClient]);
  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;

      return dateB - dateA;
    });
  }, [notifications]);

  return (
    <>
      <div className="NotificationsSectionList">
        {sortedNotifications.map(notification => (
          <article
            key={notification.id}
            onClick={() => {
              if (!notification.readed) {
                void handleMarkRead(notification);
              }

              if (notification.url) {
                window.open(notification.url, '_blank');
              }
            }}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                if (!notification.readed) {
                  void handleMarkRead(notification);
                }
              }

              if (notification.url) {
                window.open(notification.url, '_blank');
              }
            }}
            role="button"
            tabIndex={0}
            className={`NotificationsCard ${notification.readed ? 'is-read' : 'is-unread'}`}>
            <div className="NotificationsCardRail">
              {!notification.readed && <span className="NotificationsCardDot" />}
            </div>

            <div className="NotificationsCardBody">
              <div className="NotificationsCardHeader">
                <div>
                  <p className="NotificationsCardTitle">
                    {notification.title || 'Notificación sin título'}
                  </p>
                  <p className="NotificationsCardText">{notification.text}</p>
                </div>
                <Tag
                  icon={<CheckCircleFilled />}
                  color={notification.readed ? 'default' : 'blue'}
                  className="NotificationsCardTag">
                  {notification.readed ? 'Leída' : 'Nueva'}
                </Tag>
              </div>

              <div className="NotificationsCardMeta">
                <ClockCircleOutlined />
                <span>
                  {notification.created_at
                    ? dayjs(notification.created_at).format('DD MMM YYYY - HH:mm')
                    : 'Fecha no disponible'}
                </span>
              </div>
            </div>
          </article>
        ))}
      </div>
      <div className="NotificationsSectionPagination">
        <Pagination
          total={total ?? 0}
          pageSize={pageSize}
          current={currentPage}
          onChange={page => setCurrentPage(page)}
          showSizeChanger={false}
        />
      </div>
    </>
  );
};

export default NotificationsList;
